import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { SEED_DOMAIN_STARTER_INVENTORY_MUTATION } from '../graphql/seedData';

interface SeededItem {
  name: string;
  costCents: number;
}

const s = {
  page: { padding: '32px 40px', maxWidth: 700, margin: '0 auto' } as React.CSSProperties,
  title: { fontSize: 24, fontWeight: 700, margin: '0 0 8px' } as React.CSSProperties,
  subtitle: { color: '#6b7280', fontSize: 14, marginBottom: 28 } as React.CSSProperties,
  btn: { background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14 } as React.CSSProperties,
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' } as React.CSSProperties,
  error: { color: '#dc2626', fontSize: 13, marginTop: 16 } as React.CSSProperties,
  result: { marginTop: 24, border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 } as React.CSSProperties,
  resultTitle: { fontWeight: 600, marginBottom: 10, fontSize: 14 } as React.CSSProperties,
  item: { display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: '#374151' } as React.CSSProperties,
};

export default function SeedStarterData() {
  const [seedInventory, { loading }] = useMutation(SEED_DOMAIN_STARTER_INVENTORY_MUTATION);
  const [items, setItems] = useState<SeededItem[] | null>(null);
  const [error, setError] = useState('');

  const handleSeed = async () => {
    setError('');
    setItems(null);
    try {
      const res = await seedInventory();
      setItems(res.data?.seedDomainStarterInventory?.items || []);
    } catch (err: any) {
      setError(err.message?.replace('GraphQL error: ', '') || 'Failed to seed starter data.');
    }
  };

  return (
    <div style={s.page}>
      <h1 style={s.title}>Seed Starter Data</h1>
      <p style={s.subtitle}>
        Inserts the starter fabric price list (Louvolite, Mermet, Visions — priced per yard)
        into the signed-in domain as inventory items, and seeds the classic units
        (yard/inch/foot/each) if none exist yet. Does not touch labor rates or hardware — those
        are configured per-domain under Settings. Safe to run more than once — existing items are
        refreshed, nothing is duplicated.
      </p>
      <button
        style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }}
        onClick={handleSeed}
        disabled={loading}
      >
        {loading ? 'Seeding...' : 'Seed Starter Inventory'}
      </button>

      {error && <div style={s.error}>{error}</div>}

      {items && (
        <div style={s.result}>
          <div style={s.resultTitle}>Seeded {items.length} inventory items</div>
          {items.map((item) => (
            <div key={item.name} style={s.item}>
              <span>{item.name}</span>
              <span>${(item.costCents / 100).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
